import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { MonthYear } from "@common/types";
import { ICommandBase } from '@Quest/cqrs';
import { SaveViewStartedProjectsPageCommandV1 } from "@Quest/Portfolios/Domain/commands-core";
import PortfoliosCommands from "@Quest/Portfolios/Domain/commands-vue-component";
import PortfoliosQueries from "@Quest/Portfolios/Domain/queries-vue-component";
import { StartedProjectsPageReadModel } from '@Quest/Portfolios/Domain/readmodels';
import { ProjectCompletedLifecycleSetterViewModel } from "@Quest/Projects/Components/ProjectCompletedLifecycleSetter";
import ProjectCompletedLifecycleSetter from '@Quest/Projects/Components/ProjectCompletedLifecycleSetter.vue';
import { ProjectPausedLifecycleSetterViewModel } from "@Quest/Projects/Components/ProjectPausedLifecycleSetter";
import ProjectPausedLifecycleSetter from '@Quest/Projects/Components/ProjectPausedLifecycleSetter.vue';
import { ProjectResumedLifecycleSetterViewModel } from "@Quest/Projects/Components/ProjectResumedLifecycleSetter";
import ProjectResumedLifecycleSetter from '@Quest/Projects/Components/ProjectResumedLifecycleSetter.vue';
import { ProjectsGridViewModel } from "@Quest/Projects/Components/ProjectsGrid";
import ProjectsGrid from "@Quest/Projects/Components/ProjectsGrid.vue";
import { ProjectTerminatedLifecycleSetterViewModel } from "@Quest/Projects/Components/ProjectTerminatedLifecycleSetter";
import ProjectTerminatedLifecycleSetter from '@Quest/Projects/Components/ProjectTerminatedLifecycleSetter.vue';
import { SetLifecycleCompletedCommandV1, SetLifecyclePausedCommandV1, SetLifecycleResumedCommandV1, SetLifecycleTerminatedCommandV1 } from "@Quest/Projects/Domain/commands-core";
import ProjectsCommands from "@Quest/Projects/Domain/commands-vue-component";
import ProjectsQueries from '@Quest/Projects/Domain/queries-vue-component';
import { GridProjectReadModel } from '@Quest/Projects/Domain/readmodels';
import Vue from "vue";
import { Component, mixins } from 'vue-facing-decorator';


@Component({
    components: {
        PortfolioNavigation, ProjectsGrid,
        ProjectPausedLifecycleSetter, ProjectResumedLifecycleSetter,
        ProjectTerminatedLifecycleSetter, ProjectCompletedLifecycleSetter, 
    }
})
export default class StartedProjectsPage extends mixins(PortfoliosQueries, PortfoliosCommands, ProjectsCommands, ProjectsQueries)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get tchProjectId() { return Number.parseInt(this.$route.params.tchProjectId); }

    rm: StartedProjectsPageReadModel | null = null;
    get projects(): GridProjectReadModel[] { return this.rm?.Projects ?? []; }
    get portfolioId() { return this.rm.PortfolioId; }
    get portfolioName(): string | null { return this.rm?.PortfolioName; }



    grid: ProjectsGridViewModel = new ProjectsGridViewModel();

    projectPausedLifecycleSetter: ProjectPausedLifecycleSetterViewModel = new ProjectPausedLifecycleSetterViewModel();
    projectResumedLifecycleSetter: ProjectResumedLifecycleSetterViewModel = new ProjectResumedLifecycleSetterViewModel();
    projectTerminatedLifecycleSetter: ProjectTerminatedLifecycleSetterViewModel = new ProjectTerminatedLifecycleSetterViewModel();
    projectCompletedLifecycleSetter: ProjectCompletedLifecycleSetterViewModel = new ProjectCompletedLifecycleSetterViewModel();

    handleShowGridColumnsEditor()
    {
        this.grid.showGridColumnsEditor();
    }

    handleSaveViewSettings()
    {
        const grid = this.grid.getJson();
        this.invokeSaveViewStartedProjectsPageCommandV1(this.workspaceId, this.portfolioId, grid)
    }

    handleClearGridFilters()
    {
        this.grid.clearFilters();
    }

    handleExportGrid()
    {
        this.grid.exportGrid();
    }


    handleStarted(projectId: string, startDate: MonthYear)
    {
        this.invokeSetLifecycleInProgressCommandV1(this.workspaceId, this.portfolioId, projectId, startDate);
    }

    handlePaused(projectId: string)
    {
        this.invokeSetLifecyclePausedCommandV1(this.workspaceId, this.portfolioId, projectId);
    }

    handleResumed(projectId: string)
    {
        this.invokeSetLifecycleResumedCommandV1(this.workspaceId, this.portfolioId, projectId);
    }

    handleTerminated(projectId: string)
    {
        this.invokeSetLifecycleTerminatedCommandV1(this.workspaceId, this.portfolioId, projectId);
    }

    handleCompleted(projectId: string)
    {
        this.invokeSetLifecycleCompletedCommandV1(this.workspaceId, this.portfolioId, projectId);
    }


    mounted()
    {
        document.title = "Portfolio Started | TransparentChoice";
        this.runStartedProjectsPageQueryV1(this.workspaceId, this.tchProjectId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "StartedProjectsPageReadModel")
        {
            this.rm = model as StartedProjectsPageReadModel;

            Vue.nextTick(() =>
            {
                this.grid.renderGrid(this.rm.Grid.IdField, this.rm.Grid.Fields, this.rm.Grid.DefaultFieldIds, this.rm.Grid.Options);
                this.grid.setProjects(this.rm.Projects.map(p => p.Properties));
            })
        }
        else if (modelName === "GridProjectReadModel")
        {
            const rm = model as GridProjectReadModel;

            this.rm.Projects = this.rm.Projects.filter(p => p.Id !== rm.Id);
            this.rm.Projects.push(rm);

            this.grid.update(rm.Properties);
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string)
    {
        if (SaveViewStartedProjectsPageCommandV1.matchType(cmd))
        {
            alert("Saved");
        }
        else if (SetLifecyclePausedCommandV1.matchType(cmd))
        {
            const projectId = correlationId;
            this.runGridProjectByIdQueryV1(this.workspaceId, this.portfolioId, projectId);

            this.projectPausedLifecycleSetter.close();
        }
        else if (SetLifecycleResumedCommandV1.matchType(cmd))
        {
            const projectId = correlationId;
            this.runGridProjectByIdQueryV1(this.workspaceId, this.portfolioId, projectId);

            this.projectResumedLifecycleSetter.close();
        }
        else if (SetLifecycleTerminatedCommandV1.matchType(cmd))
        {
            const projectId = correlationId;

            this.rm.Projects = this.rm.Projects.filter(a => a.Id !== projectId);
            this.grid.removeRowById(projectId);

            this.projectTerminatedLifecycleSetter.close();
        }
        else if (SetLifecycleCompletedCommandV1.matchType(cmd))
        {
            const projectId = correlationId;

            this.rm.Projects = this.rm.Projects.filter(a => a.Id !== projectId);
            this.grid.removeRowById(projectId);

            this.projectCompletedLifecycleSetter.close();
        }
    }
}